import { useState } from "react";
import type { Deal } from "../types";
import { calc } from "../engine/engine";
import { project, amortSplit, HORIZONS } from "../lib/projection";
import { money0, pct } from "../lib/format";
import { LineChart, StackChart, BarChart } from "./Charts";

const C = {
  value: "#17352B", loan: "#A6503C", equity: "#3E7A5E",
  initial: "#2C5C4A", paydown: "#3E7A5E", appr: "#B08948", va: "#C8A45E",
};

function Kpi({ label, value, sub, good }: { label: React.ReactNode; value: string; sub?: string; good?: boolean }) {
  return (
    <div className="card kpi">
      <div className="kl">{label}</div>
      <div className="kv">{value}</div>
      {sub && <div className={"ks" + (good === true ? " good" : good === false ? " bad" : "")}>{sub}</div>}
    </div>
  );
}

export function Projections({ deal }: { deal: Deal }) {
  const m = calc(deal);
  const [horizon, setHorizon] = useState<number>(5);
  const [bump, setBump] = useState<number>(50);
  const p = project(deal, m, horizon, bump);
  const years = p.rows.map((r) => r.year);
  const end = p.end;

  const growth = end.equity - p.e0;
  const nextBuyingPower = p.cashOutAtEnd / 0.25; // 25% down on the next deal
  const s1 = amortSplit(deal, m, 1);

  return (
    <>
      <div className="pe">
        <div className="l">◆ In plain English</div>
        <p>
          This simulates how <b>wealth compounds</b> on this property. Equity grows three ways at once: the tenant
          <b> pays down your loan</b>, the market <b>appreciates</b>, and <b>you force value</b> by raising rents.
          At the market cap rate, every extra dollar of yearly rent becomes <b>{money0(1 / deal.exitCap)}</b> of value —
          equity you can refinance out to buy the next one.
        </p>
      </div>

      {/* controls */}
      <div className="card pad" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Horizon</div>
            <div className="depthbar" style={{ border: "1px solid var(--line)", borderRadius: 100 }}>
              {HORIZONS.map((h) => (
                <button key={h} className={horizon === h ? "on" : ""} style={{ color: horizon === h ? "#241a08" : "var(--forest)", padding: "6px 14px" }} onClick={() => setHorizon(h)}>
                  {h} yr
                </button>
              ))}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Value-add — rent bump <span className="mono" style={{ color: "var(--brass-deep)" }}>+{money0(bump)}/unit/mo</span></div>
            <input type="range" min={0} max={200} step={5} value={bump} onChange={(e) => setBump(+e.target.value)} style={{ width: "100%", accentColor: "var(--brass)" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "var(--muted)" }}>
              <span>$0</span><span>renovate &amp; raise rents →</span><span>$200</span>
            </div>
          </div>
        </div>
      </div>

      {/* headline KPIs at the selected horizon */}
      <div className="grid g4">
        <Kpi label={`Value in ${horizon} yr`} value={money0(end.value)} sub={money0(end.value - p.v0) + " vs today"} good={end.value > p.v0} />
        <Kpi label={`Equity in ${horizon} yr`} value={money0(end.equity)} sub={"from " + money0(p.e0) + " today"} good />
        <Kpi label="Wealth added" value={money0(growth)} sub={pct(growth / Math.max(1, p.e0), 0) + " on starting equity"} good />
        <Kpi label="Loan balance" value={money0(end.loanBalance)} sub={money0(end.principalPaid) + " paid off"} />
      </div>

      {/* value & equity over time */}
      <h3 className="sechead" style={{ fontSize: 17 }}>Property value, loan &amp; equity over {horizon} years</h3>
      <div className="card pad">
        <LineChart
          years={years}
          series={[
            { name: "Property value", color: C.value, values: p.rows.map((r) => r.value) },
            { name: "Equity", color: C.equity, values: p.rows.map((r) => r.equity), fill: true },
            { name: "Loan balance", color: C.loan, values: p.rows.map((r) => r.loanBalance), dashed: true },
          ]}
        />
      </div>

      {/* equity composition */}
      <h3 className="sechead" style={{ fontSize: 17 }}>Where the equity comes from</h3>
      <div className="grid g2" style={{ alignItems: "start" }}>
        <div className="card pad">
          <StackChart
            years={years}
            layers={[
              { name: "Starting equity", color: C.initial, values: p.rows.map((r) => r.eqInitial) },
              { name: "Loan paydown", color: C.paydown, values: p.rows.map((r) => r.eqPaydown) },
              { name: "Market appreciation", color: C.appr, values: p.rows.map((r) => r.eqAppreciation) },
              { name: "Forced value-add", color: C.va, values: p.rows.map((r) => r.eqValueAdd) },
            ]}
          />
        </div>
        <div className="card pad">
          <div className="eyebrow">At year {horizon}, your {money0(end.equity)} of equity is</div>
          <table className="t" style={{ marginTop: 6 }}>
            <tbody>
              <Row c={C.initial} l="Money you put in" v={money0(end.eqInitial)} />
              <Row c={C.paydown} l="Loan paid down (by tenants)" v={money0(end.eqPaydown)} />
              <Row c={C.appr} l="Market appreciation" v={money0(end.eqAppreciation)} />
              <Row c={C.va} l={`Forced value-add (+${money0(bump)}/unit)`} v={money0(end.eqValueAdd)} />
              <tr className="tot"><td>Total equity</td><td className="n">{money0(end.equity)}</td></tr>
            </tbody>
          </table>
          <div className="note">Cumulative cash flow over {horizon} yr: <b>{money0(end.cumCashFlow)}</b> on top of the equity.</div>
        </div>
      </div>

      {/* the snowball — refinance to the next deal */}
      <h3 className="sechead" style={{ fontSize: 17 }}>The snowball → your next property</h3>
      <div className="card pad" style={{ borderLeft: "4px solid var(--brass)" }}>
        <div className="grid g3">
          <Kpi label={`Refi value @ ${pct(p.refiLTV, 0)} LTV`} value={money0(p.maxLoanAtEnd)} sub={"on a " + money0(end.value) + " building"} />
          <Kpi label="Cash you can pull out" value={money0(p.cashOutAtEnd)} sub="tax-free refinance proceeds" good />
          <Kpi label="Funds a next purchase of" value={money0(nextBuyingPower)} sub="at 25% down" good />
        </div>
        <div className="flag ok" style={{ marginTop: 10 }}>
          <span>✓</span>
          <div>
            In <b>{horizon} year{horizon > 1 ? "s" : ""}</b>, refinance this building at {pct(p.refiLTV, 0)} LTV and pull out about <b>{money0(p.cashOutAtEnd)}</b> —
            enough for the down payment on a <b>~{money0(nextBuyingPower)}</b> property #{"2"}. Repeat to compound into #3, #4…
            {bump > 0 && <> The <b>+{money0(bump)}/unit</b> rent bump alone adds <b>{money0(end.eqValueAdd)}</b> of pullable equity.</>}
          </div>
        </div>
      </div>

      {/* amortization + cash flow */}
      <h3 className="sechead" style={{ fontSize: 17 }}>Amortization &amp; cash flow</h3>
      <div className="grid g2" style={{ alignItems: "start" }}>
        <div className="card pad">
          <div className="eyebrow">Annual cash flow (NOI − debt service)</div>
          <BarChart years={years} values={p.rows.map((r) => r.cashFlow)} color={C.initial} />
        </div>
        <div className="card pad">
          <div className="eyebrow">Loan amortization</div>
          <table className="t" style={{ marginTop: 6 }}>
            <tbody>
              <Row l="Starting loan" v={money0(p.l0)} />
              <Row l="Year-1 payment → principal" v={money0(s1.principal)} />
              <Row l="Year-1 payment → interest" v={money0(s1.interest)} />
              <Row l={`Balance after ${horizon} yr`} v={money0(end.loanBalance)} />
              <tr className="tot"><td>Principal retired</td><td className="n">{money0(end.principalPaid)}</td></tr>
            </tbody>
          </table>
          <div className="note">On a {deal.amort}-yr amortization at {pct(deal.permRate)}, early payments are mostly interest; the paydown accelerates every year.</div>
        </div>
      </div>
    </>
  );
}

function Row({ l, v, c }: { l: React.ReactNode; v: string; c?: string }) {
  return (
    <tr>
      <td>{c && <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: c, marginRight: 7 }} />}{l}</td>
      <td className="n">{v}</td>
    </tr>
  );
}
